import type { ThreadId } from "@afar-research/contracts";

import type { SessionPhase } from "../../types";

/**
 * Send-busy is a local stand-in for an acknowledgement that has not arrived
 * yet. A send begins a local dispatch, and the snapshot clears itself once the
 * Environment's own state for the thread being viewed moves past it.
 *
 * Three sends used to answer "and when do I clear it myself?" three different
 * ways, none of them written down: the composer cleared it conditionally, the
 * plan follow-up left it alone, and the plan implementation always cleared it.
 * They were not three rules. They were one rule reached from three directions.
 */

/** The effects a send performs on its own lifecycle state. */
export interface OutgoingSendLifecyclePorts {
  /** Marks a send as occupying this thread, so a concurrent one declines. */
  readonly setInFlight: (inFlight: boolean) => void;
  /** Records the local stand-in for a not-yet-arrived acknowledgement. */
  readonly beginLocalDispatch: (options?: { readonly preparingWorktree?: boolean }) => void;
  /** Drops that stand-in. */
  readonly resetLocalDispatch: () => void;
}

/** What a send knows about its own outcome once it is done dispatching. */
export interface OutgoingSendOutcome {
  /** Whether the Environment accepted a turn start. */
  readonly turnStarted: boolean;
  /** The thread the turn was dispatched to. */
  readonly dispatchedTo: ThreadId;
  /** The thread being viewed, whose state supplies the acknowledgement. */
  readonly activeThreadId: ThreadId | null;
  /** The viewed thread's phase, which decides how its acknowledgement reads. */
  readonly phase: SessionPhase;
}

/**
 * Release the local stand-in exactly when the acknowledgement for the viewed
 * thread cannot clear it:
 *
 * - nothing started, so nothing will ever acknowledge it — and an interrupted
 *   failure sets no thread error, so nothing else would clear it either;
 * - the viewed thread already has a running turn, so this dispatch enqueued
 *   behind it and the acknowledgement cannot be told apart from the running
 *   turn's own; leaving it strands the Thread Queue on "Queueing…";
 * - the turn started on a *different* thread, so the viewed thread's state
 *   will never move at all.
 */
export function shouldReleaseLocalDispatch(outcome: OutgoingSendOutcome): boolean {
  return (
    !outcome.turnStarted ||
    outcome.phase === "running" ||
    outcome.dispatchedTo !== outcome.activeThreadId
  );
}

export interface OutgoingSendLifecycle {
  /** Claims the thread and records the local stand-in. */
  readonly begin: (options?: { readonly preparingWorktree?: boolean }) => void;
  /** Releases the thread, and the stand-in when nothing will clear it. */
  readonly finish: (outcome: OutgoingSendOutcome) => void;
}

export function createOutgoingSendLifecycle(
  ports: OutgoingSendLifecyclePorts,
): OutgoingSendLifecycle {
  return {
    begin: (options) => {
      ports.setInFlight(true);
      ports.beginLocalDispatch(options);
    },
    finish: (outcome) => {
      ports.setInFlight(false);
      if (shouldReleaseLocalDispatch(outcome)) {
        ports.resetLocalDispatch();
      }
    },
  };
}
